'use client';

import React, { useCallback, useState } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { buildShareUrl } from '@/utils';
import { SHARE_UTM } from '../_const/share';
import linkShareIcon from '@images/link-share.svg';
import CopiedBadgeIcon from './copied-badge-icon';

// 토스트·배지 아이콘은 motion 을 쓰므로 지연 로드한다 — 공유 버튼을 누르지 않는
// 대다수 방문자의 블로그 상세 초기 번들에 모션 런타임이 얹히지 않게 한다
const CopyLinkToast = dynamic(() => import('./copy-link-toast'), {
  ssr: false,
});

/**
 * 인라인 배지의 크기 — 청크가 도착하기 전에도 자리를 잡아 둔다.
 *
 * `-top-[0.5px]` 은 옆 문구와의 광학 중심을 맞추는 보정이다. 배지 자체(루트가
 * 이미 relative)만 움직이므로 레이아웃 높이는 그대로다. 데스크톱 토스트는 같은
 * 컴포넌트를 쓰지만 크기가 달라 보정이 필요 없어 여기(호출부)에만 건다.
 */
const INLINE_BADGE_CLASSNAME = '-top-[0.5px] h-[19.17px] w-5';

/** 복사 완료 토스트 유지 시간 */
const COPY_FEEDBACK_MS = 2000;

/**
 * "이 아티클 공유하기" — 현재 글 URL 을 클립보드에 복사한다.
 *
 * 데스크톱은 좌 레일, 모바일은 본문 하단에 놓인다(폭은 부모가 정한다).
 *
 * 복사 완료를 알리는 방식이 브레이크포인트마다 다르다 — 모바일은 버튼 안에서
 * 인라인으로 바뀌고(Figma 2679-71229), 데스크톱은 화면 상단 토스트를 띄운다.
 */
export default function ShareArticleButton() {
  const t = useTranslations('Blog.main');
  const [isCopied, setIsCopied] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  // 첫 복사 이후로는 마운트를 유지한다 — 언마운트하면 AnimatePresence 가 함께
  // 사라져 토스트의 exit 애니메이션이 재생될 틈이 없다
  const [hasCopiedOnce, setHasCopiedOnce] = useState(false);

  const handleClick = useCallback(async () => {
    if (isCopied) return;

    const url = buildShareUrl(SHARE_UTM);
    try {
      await navigator.clipboard.writeText(url);
      setHasCopiedOnce(true);
      setIsCopied(true);
      setIsFailed(false);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_MS);
    } catch (error) {
      console.error('Failed to copy article URL:', error);
      // 클립보드 실패(비보안 컨텍스트·Safari 권한 거부 등)도 라이브 리전으로 알려야
      // 스크린리더 사용자에게 아무 피드백도 없는 상태가 되지 않는다
      setIsFailed(true);
      setTimeout(() => setIsFailed(false), COPY_FEEDBACK_MS);
    }
  }, [isCopied]);

  return (
    <div className="w-full">
      <TrackedButton
        label="share_article"
        position={TRACKED_EVENT_POSITION.LEFT_SIDEBAR}
        type="button"
        onClick={handleClick}
        className="flex h-10 w-full items-center justify-center rounded-[10px] bg-3b-background-medium px-2.5 transition-colors hover:bg-3b-background-200"
      >
        {/* 모바일에서 복사되면 이 기본 내용이 아래 완료 내용으로 교체된다.
            데스크톱은 좌 레일에 있어 버튼이 좁아 상단 토스트가 계속 맡는다 */}
        <span
          className={
            isCopied
              ? 'hidden items-center gap-1 xl:flex'
              : 'flex items-center gap-1'
          }
        >
          <span className="flex size-5 items-center justify-center">
            <Image
              src={linkShareIcon}
              alt=""
              width={15}
              height={15}
              aria-hidden
            />
          </span>
          <span className="text-[14px] font-semibold leading-4 tracking-[-0.128px] text-3b-text-primary">
            {t('shareArticle')}
          </span>
        </span>
        {isCopied ? (
          <span className="flex items-center gap-[7px] xl:hidden">
            {/* 토스트와 같은 배지를 20×19.17px 로 줄여 쓴다 */}
            <CopiedBadgeIcon className={INLINE_BADGE_CLASSNAME} />
            <span className="text-[14px] font-semibold leading-4 tracking-[-0.128px] text-3b-text-primary">
              {t('shareArticleCopied')}
            </span>
          </span>
        ) : null}
      </TrackedButton>
      {/* 성공·실패 모두 이 상주 리전이 알린다 — 토스트처럼 리전 자체가 DOM 에
          새로 삽입되는 형태면 일부 스크린리더 조합에서 낭독이 누락된다 */}
      <span role="status" aria-live="polite" className="sr-only">
        {isCopied ? t('shareArticleCopied') : null}
        {isFailed ? t('shareArticleFailed') : null}
      </span>
      {hasCopiedOnce ? (
        <CopyLinkToast isOpen={isCopied} message={t('shareArticleCopied')} />
      ) : null}
    </div>
  );
}
